import { observable, action, makeObservable } from "mobx";
import { cashOutConfirmationResetTime, getFunctionHandler, telemetry } from "../../constants";
import { safeString, Tracker, conditionalMap } from "../../helpers";
import { mapInitialResponse, proxyMsgPackMapper, mapUpdateResponse, mapCashoutOperationPull, cashoutOperationsUpdate } from "../BetsRetriever/mappers";
import { getTruthyValue } from "@draftkings/sportsbook-common-utils";
export class CashOutRetriever {
    constructor(options) {
        this.onData = (data) => {
            const messagaHandler = this.pushMessageHandler.get(getTruthyValue(data.id, "")) || this.pullMessageHandlersMap[data.type];
            messagaHandler === null || messagaHandler === void 0 ? void 0 : messagaHandler(data);
        };
        this.handleInitialResponse = (response) => {
            var _a;
            const result = "result" in response.data ? response.data.result.initial : response.data;
            const data = conditionalMap(this.isMsgpack, mapInitialResponse, result);
            const cashOutData = data.cashOut;
            this.handleData({
                type: "getInformations",
                data: Object.values(cashOutData.informations),
            });
            const operations = this.filterOperations(Object.values(cashOutData.operations));
            if (response.id) {
                this.cashoutInformationSubscriptionId = response.id;
                this.pushMessageHandler.set(response.id, this.handleInformationsUpdate.bind(this));
            }
            if (operations.length > 0) {
                this.cashoutOperaitionSubscription = true;
                (_a = this.myBetsSubscription) === null || _a === void 0 ? void 0 : _a.send({
                    method: "cashOutOperations",
                    params: {
                        initial: true,
                    },
                });
                this.updateCashOutOperations(operations);
            }
        };
        this.filterOperations = (operations) => operations.filter(operation => operation.status !== "Accepted" && operation.status !== "Declined");
        this.handleInformationsUpdate = (response) => {
            const updates = conditionalMap(this.isMsgpack, proxyMsgPackMapper(mapUpdateResponse, response.data), response.data.result);
            if ("cashOutUpdate" in updates) {
                this.handleData({
                    type: "informationsUpdate",
                    data: updates.cashOutUpdate,
                });
            }
        };
        this.handleOperationsUpdate = (response) => {
            const updates = conditionalMap(this.isMsgpack, proxyMsgPackMapper(cashoutOperationsUpdate, response.data), response.data.result);
            if ("cashOutOperationResultUpdate" in updates) {
                this.handleData({
                    type: "operationsUpdate",
                    data: updates.cashOutOperationResultUpdate,
                });
            }
        };
        this.handleOperationsPull = (response) => {
            const data = "result" in response.data ? response.data.result : response.data;
            const result = conditionalMap(this.isMsgpack, mapCashoutOperationPull, data);
            if ("initial" in result) {
                const operations = this.filterOperations(Object.values(result.initial));
                this.updateCashOutOperations(operations);
            }
            if (response.id) {
                this.cashoutOperaitionSubscription = true;
                this.cashoutOperaitionSubscriptionId = response.id;
                this.pushMessageHandler.set(response.id, this.handleOperationsUpdate.bind(this));
            }
        };
        this.activate = (myBetsSubscription) => {
            this.myBetsSubscription = myBetsSubscription;
            this.subscription = this.connection.subscribe({
                name: "CashOut",
                params: undefined,
                onData: this.handleData,
                onError: this.handleError,
            });
        };
        this.deactivate = () => {
            var _a;
            (_a = this.subscription) === null || _a === void 0 ? void 0 : _a.dispose();
            this.myBetsSubscription = undefined;
            this.subscription = undefined;
            this.cashOutInformations.clear();
            this.cashOutOperations.clear();
            this.pushMessageHandler.clear();
            this.cashoutInformationSubscriptionId = "";
            this.cashoutOperaitionSubscriptionId = "";
            this.cashoutOperaitionSubscription = false;
            Object.keys(this.cashOutConfirmationsPending).forEach(key => {
                clearTimeout(this.cashOutConfirmationsPending[key]);
            });
            this.cashOutConfirmationsPending = {};
        };
        this.handleReconnect = () => {
            this.pushMessageHandler.delete(this.cashoutOperaitionSubscriptionId);
            this.pushMessageHandler.delete(this.cashoutInformationSubscriptionId);
            this.cashoutOperaitionSubscriptionId = "";
            this.cashoutInformationSubscriptionId = "";
            this.cashoutOperaitionSubscription = false;
        };
        this.requestCashOut = (request) => {
            var _a;
            (_a = this.subscription) === null || _a === void 0 ? void 0 : _a.send({
                method: "cashOutBet",
                params: request,
            });
        };
        this.acceptCashOutNewOffer = (request) => {
            var _a;
            (_a = this.subscription) === null || _a === void 0 ? void 0 : _a.send({
                method: "acceptNewOffer",
                params: request,
            });
        };
        this.handleData = (payload) => {
            this.mapOnDataHandlers[payload.type](payload);
        };
        this.handleError = (error) => {
            var _a, _b;
            /* istanbul ignore next */
            (_b = (_a = this.mapOnErrorHandlers)[error.type]) === null || _b === void 0 ? void 0 : _b.call(_a, error);
        };
        this.updateCashOutOperation = (payload) => {
            var _a;
            const operation = payload.data;
            if (!this.cashoutOperaitionSubscription && !this.cashoutOperaitionSubscriptionId) {
                (_a = this.myBetsSubscription) === null || _a === void 0 ? void 0 : _a.send({
                    method: "cashOutOperations",
                    params: {
                        initial: false
                    }
                });
            }
            this.updateCashOutOperations([operation]);
        };
        this.handleOperationsError = (payload, priorityCashOutStatus) => {
            const error = payload.data;
            this.deleteCashOutOperation(safeString(error.betId));
            if (typeof error.expectedValue !== "undefined") {
                const information = this.cashOutInformations.get(safeString(error.betId));
                if (typeof information !== "undefined") {
                    this.updateCashOutInformations([Object.assign(Object.assign({}, information), { cashOutStatus: "Invalid", fullReturn: error.expectedValue })]);
                }
            }
            else {
                const information = this.cashOutInformations.get(safeString(error.betId));
                if (typeof information !== "undefined") {
                    const cashOutStatus = information.cashOutStatus === "Enabled" ? "Error" : information.cashOutStatus;
                    this.updateCashOutInformations([Object.assign(Object.assign({}, information), { cashOutStatus: priorityCashOutStatus || cashOutStatus })]);
                }
            }
        };
        this.handleOperationResultUpdate = (payload) => {
            const operation = payload.data;
            this.mapCashOutOperationHandlers[operation.status](payload);
        };
        this.handleAcceptedCashOutOperation = (payload) => {
            const operation = payload.data;
            const betId = operation.cashOutOperation.cashOutInformation.betId;
            this.deleteCashOutOperation(betId);
            this.deleteCashOutInformation(betId);
            this.onBetCashedOut(operation);
            this.shouldUnsubscribe();
        };
        this.shouldUnsubscribe = () => {
            if (this.myBetsSubscription && this.cashOutOperations.size < 1 && this.cashoutOperaitionSubscription && this.cashoutOperaitionSubscriptionId) {
                this.myBetsSubscription.send({
                    method: "manualUnsubscribe",
                    params: {
                        id: this.cashoutOperaitionSubscriptionId
                    },
                });
                this.cashoutOperaitionSubscription = false;
                this.cashoutOperaitionSubscriptionId = "";
            }
        };
        this.handleDeclinedCashOutOperation = (payload) => {
            const operation = payload.data;
            const betId = operation.cashOutOperation.cashOutInformation.betId;
            const information = this.cashOutInformations.get(betId);
            if (typeof information !== "undefined" && information.cashOutStatus === "Processing") {
                this.updateCashOutInformations([Object.assign(Object.assign({}, information), { cashOutStatus: "Enabled" })]);
            }
            this.deleteCashOutOperation(betId);
            this.shouldUnsubscribe();
        };
        this.handleCashOutInformationsUpdate = (payload) => {
            const informations = payload.data;
            this.updateCashOutInformations(informations.map(information => (Object.assign(Object.assign({}, information), { cashOutStatus: information.cashOutEnabled ? "Enabled" : information.cashOutStatus }))));
        };
        this.handleCashOutInformationMessage = (payload) => {
            const information = payload.data;
            const previousInformation = this.cashOutInformations.get(information.betId);
            const operation = this.cashOutOperations.get(information.betId);
            if ((previousInformation === null || previousInformation === void 0 ? void 0 : previousInformation.cashOutStatus) === "Confirm" && typeof operation === "undefined") {
                Tracker.getInstance().trackEvent(telemetry.events.cashOutProcessing, {
                    Action: telemetry.actions.cashOut.oddsChangeAfterUserInitiateCashout,
                    MyBetsVersion: telemetry.version,
                    ResponseBetId: information.betId,
                    PurchaseId: information.purchaseId,
                    Status: information.cashOutStatus,
                    ClientType: telemetry.clientType,
                });
            }
            this.updateCashOutInformations([information]);
        };
        this.resetConfirmationStatus = (betId) => {
            clearTimeout(this.cashOutConfirmationsPending[betId]);
            delete this.cashOutConfirmationsPending[betId];
            this.cashOutConfirmationsPending[betId] = window.setTimeout(() => {
                const cashOutInformation = this.cashOutInformations.get(betId);
                //Ignoring next line due to complex mocking and simulating of behavior, which won't provide useful tests
                /* istanbul ignore next */
                const cashOutOperation = this.cashOutOperations.get(betId);
                //Ignoring next line due to complex mocking and simulating of behavior, which won't provide useful tests
                /* istanbul ignore next */
                if (typeof cashOutOperation === "undefined" && (cashOutInformation === null || cashOutInformation === void 0 ? void 0 : cashOutInformation.cashOutStatus) === "Confirm") {
                    this.updateCashOutInformations([Object.assign(Object.assign({}, cashOutInformation), { cashOutStatus: "Enabled" })]);
                }
            }, cashOutConfirmationResetTime);
        };
        this.cashOutInformations = observable.map(new Map());
        this.cashOutOperations = observable.map(new Map());
        this.connection = options.connection;
        this.onBetCashedOut = options.onBetCashedOut;
        this.cashOutConfirmationsPending = {};
        this.myBetsSubscription = undefined;
        this.cashoutOperaitionSubscription = false;
        this.cashoutOperaitionSubscriptionId = "";
        this.cashoutInformationSubscriptionId = "";
        this.isMsgpack = options.isMsgpack;
        this.pushMessageHandler = new Map();
        this.pullMessageHandlersMap = {
            initialBetsPage: (payload) => this.handleInitialResponse(payload),
            cashOutOperations: (payload) => this.handleOperationsPull(payload),
        };
        this.mapCashOutOperationHandlers = new Proxy({
            Accepted: this.handleAcceptedCashOutOperation,
            Declined: this.handleDeclinedCashOutOperation,
            LiveDelay: this.updateCashOutOperation,
            TraderDelay: this.updateCashOutOperation,
            NewOffer: this.updateCashOutOperation,
        }, getFunctionHandler);
        this.mapOnDataHandlers = new Proxy({
            getInformations: this.handleCashOutInformationsUpdate,
            informationsUpdate: this.handleCashOutInformationMessage,
            operationsUpdate: this.handleOperationResultUpdate,
            acceptNewOffer: this.handleOperationResultUpdate,
            cashOutBet: this.handleOperationResultUpdate
        }, getFunctionHandler);
        this.mapOnErrorHandlers = new Proxy({
            acceptNewOffer: (payload) => this.handleOperationsError(payload),
            cashOutBet: (payload) => this.handleOperationsError(payload, "Error"),
        }, getFunctionHandler);
        makeObservable(this, {
            updateCashOutInformationStatus: action.bound,
            updateCashOutOperationStatus: action.bound,
            updateCashOutInformations: action.bound,
            updateCashOutOperations: action.bound,
            deleteCashOutOperation: action.bound,
            deleteCashOutInformation: action.bound,
        });
    }
    updateCashOutInformationStatus(betId, newStatus) {
        const information = this.cashOutInformations.get(betId);
        if (typeof information !== "undefined") {
            this.cashOutInformations.set(betId, Object.assign(Object.assign({}, information), { cashOutStatus: newStatus }));
            this.resetConfirmationStatus(betId);
        }
    }
    updateCashOutOperationStatus(betId, newStatus) {
        const operation = this.cashOutOperations.get(betId);
        if (typeof operation !== "undefined") {
            this.cashOutOperations.set(betId, Object.assign(Object.assign({}, operation), { status: newStatus }));
        }
    }
    deleteCashOutOperation(betId) {
        this.cashOutOperations.delete(betId);
        this.shouldUnsubscribe();
    }
    deleteCashOutInformation(betId) {
        this.cashOutInformations.delete(betId);
    }
    updateCashOutInformations(informations) {
        informations.forEach(information => {
            this.cashOutInformations.set(information.betId, information);
        });
    }
    updateCashOutOperations(operations) {
        operations.forEach(operation => {
            this.cashOutOperations.set(operation.cashOutOperation.cashOutInformation.betId, operation);
        });
    }
}
//# sourceMappingURL=CashOutRetriever.js.map